import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  FiCheck, FiX, FiAlertCircle,
  FiShield, FiCreditCard, FiGlobe, FiServer
} from 'react-icons/fi';
import { creditBureauService } from '../../services/creditBureauService';

const CreditBureauSettings = () => {
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const config = useMemo(() => {
    const provider = import.meta.env?.VITE_CREDIT_PROVIDER || 'demo';
    const isSandbox = import.meta.env?.VITE_700CREDIT_SANDBOX === 'true';
    
    return {
      provider,
      isSandbox,
      accounts: [
        {
          label: 'Credit (Hard Pull)',
          account: creditBureauService.creditAccount,
          hasPassword: !!creditBureauService.creditPassword,
        },
        {
          label: 'Prequalify (Soft Pull)',
          account: creditBureauService.pqAccount,
          hasPassword: !!creditBureauService.pqPassword,
        },
        {
          label: 'QuickScan',
          account: creditBureauService.scanAccount,
          hasPassword: !!creditBureauService.scanPassword,
        },
      ],
      endpoint: isSandbox
        ? 'gateway.700creditsolution.com (Test)'
        : 'gateway.700dealer.com (Production)',
    };
  }, []);

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const testApplicant = {
        firstName: 'Test',
        lastName: 'Connection',
        ssn: '000-00-0000',
        dob: '1990-01-01',
        address: '123 Test St',
        city: 'Dallas',
        state: 'TX',
        zip: '75001',
      };
      
      const result = await creditBureauService.softPull(testApplicant, { bureau: 'TU' });
      
      setTestResult({
        success: true,
        message: result.score 
          ? `Connected. Test score: ${result.score}` 
          : 'Connected to 700Credit gateway.',
      });
    } catch (err) {
      console.error('Connection test failed:', err);
      setTestResult({
        success: false,
        message: err.message,
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Credit Bureau Configuration</h2>
        <p className="text-gray-600">700Credit API status and account details</p>
      </div>

      {/* Status Banner */}
      <div className={`rounded-lg p-4 mb-6 ${
        config.isSandbox 
          ? 'bg-yellow-50 border border-yellow-200' 
          : 'bg-green-50 border border-green-200'
      }`}>
        <div className="flex items-center gap-3">
          <FiShield className={`text-xl ${config.isSandbox ? 'text-yellow-600' : 'text-green-600'}`} />
          <div>
            <p className={`font-semibold ${config.isSandbox ? 'text-yellow-800' : 'text-green-800'}`}>
              {config.isSandbox ? 'Sandbox Mode (Testing)' : 'Production Mode (Live)'}
            </p>
            <p className={`text-sm ${config.isSandbox ? 'text-yellow-700' : 'text-green-700'}`}>
              {config.isSandbox 
                ? 'Credit pulls hit the test gateway. No real credit checks.' 
                : 'Credit pulls are LIVE. Real credit checks with real costs.'}
            </p>
          </div>
        </div>
      </div>

      {/* Account Configuration */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FiCreditCard className="text-blue-600" />
          Product Accounts
        </h3>
        <div className="space-y-3">
          {config.accounts.map((acct) => (
            <div key={acct.label} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-800">{acct.label}</p>
                <p className="text-sm text-gray-500">Account: {acct.account}</p>
              </div>
              <div className="flex items-center gap-2">
                {acct.hasPassword ? (
                  <span className="flex items-center gap-1 text-green-600 text-sm">
                    <FiCheck /> Configured
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-red-600 text-sm">
                    <FiX /> Missing
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Connection Details */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FiGlobe className="text-blue-600" />
          Connection Details
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-800">API Endpoint</p>
              <p className="text-sm text-gray-500">{config.endpoint}</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-800">Proxy</p>
              <p className="text-sm text-gray-500">Fixie Static IP (criterium.usefixie.com)</p>
            </div>
            <span className="flex items-center gap-1 text-green-600 text-sm">
              <FiCheck /> Active
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-800">Static Outbound IPs</p>
              <p className="text-sm text-gray-500">52.5.155.132, 52.87.82.133</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-800">Edge Function</p>
              <p className="text-sm text-gray-500">Supabase credit-pull</p>
            </div>
            <span className="flex items-center gap-1 text-green-600 text-sm">
              <FiServer /> Deployed
            </span>
          </div>
        </div>
      </div>

      {/* Production Checklist */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Production Checklist</h3>
        <div className="space-y-2 text-sm">
          <ChecklistItem done={true} label="Test credentials configured" />
          <ChecklistItem done={true} label="Supabase Edge Function updated" />
          <ChecklistItem done={true} label="Fixie static IP proxy set up" />
          <ChecklistItem done={false} label="Send IPs to 700Credit for whitelisting" />
          <ChecklistItem done={false} label="Schedule final review with Ken Hill" />
          <ChecklistItem done={false} label="Switch to production credentials" />
          <ChecklistItem done={false} label="Set CREDIT_700_SANDBOX to false" />
        </div>
      </div>

      {/* Test Result */}
      {testResult && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-lg ${
            testResult.success 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {testResult.success ? (
              <FiCheck className="text-green-600" />
            ) : (
              <FiX className="text-red-600" />
            )}
            <p className={`text-sm font-medium ${
              testResult.success ? 'text-green-800' : 'text-red-800'
            }`}>
              {testResult.message}
            </p>
          </div>
        </motion.div>
      )}

      {/* Test Button */}
      <button
        onClick={testConnection}
        disabled={testing}
        className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 flex items-center gap-2"
      >
        {testing ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            Testing...
          </>
        ) : (
          <>
            <FiShield />
            Test Connection
          </>
        )}
      </button>

      {/* Config Note */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <FiAlertCircle className="text-blue-600 mt-0.5" />
          <p className="text-sm text-blue-800">
            Credentials are configured in the <code className="bg-blue-100 px-1 rounded">.env</code> file 
            and Supabase Edge Function secrets. Contact your developer to update them.
          </p>
        </div>
      </div>
    </div>
  );
};

const ChecklistItem = ({ done, label }) => (
  <div className="flex items-center gap-2 p-2">
    {done ? (
      <FiCheck className="text-green-500 flex-shrink-0" />
    ) : (
      <div className="w-4 h-4 border-2 border-gray-300 rounded flex-shrink-0" />
    )}
    <span className={done ? 'text-gray-600' : 'text-gray-800 font-medium'}>{label}</span>
  </div>
);

export default CreditBureauSettings;
